import { createApi } from "@reduxjs/toolkit/query/react";
import endpoints, { baseURL } from "data/api/v1/endpoints.ts";
import { baseQueryWithRedirect, encodePostRequest } from "features/api/utils/apiUtils.ts";
import { GetAccountsResponse, GetAccountsResponseData } from "types/api/accounts/getAccounts.ts";
import { BillToInfoResponse, BillToInfoResponseData, BillTosRequestParameters } from "types/api/accounts/billToInfo.ts";
import { models } from "types/api/viewModels.ts";
import {
	GetEmulationAccountsQueryParameters,
	GetEmulationAccountsResponse,
	GetEmulationAccountsResponseData
} from "types/api/accounts/getEmulationAccounts.ts";
import {
	AddMerchandiseShipToResponse,
	AddMerchandiseShipToResponseData,
	AddNewMerchandiseShipToRequest,
	MerchandiseShipTosResponse,
	MerchandiseShipTosResponseData
} from "features/MarketingMaterials/api/merchandiseShipTos.ts";

interface BillToShipToParams {
	accountId: string;
	billToId: string;
}
export const accountApiSlice = createApi({
	reducerPath: "accountApi",
	baseQuery: baseQueryWithRedirect(baseURL),
	tagTypes: ["BillTo", "Accounts", "MerchandiseShipTos"],
	endpoints: (builder) => ({
		getAccounts: builder.query<GetAccountsResponseData, string | undefined | null>({
			query: (productLineCode?: string | null) =>
				endpoints.accounts.getAccounts(productLineCode ? encodeURIComponent(productLineCode) : null),
			transformResponse: (response: GetAccountsResponse) => response?.data,
			providesTags: ["Accounts"]
		}),
		getAccountsById: builder.query<GetAccountsResponseData, string>({
			query: (orderId: string) => endpoints.accounts.getAccountsByOrderId(orderId),
			transformResponse: (response: GetAccountsResponse) => response?.data
		}),
		getTrucksAccounts: builder.query<GetAccountsResponseData, string>({
			query: () => endpoints.accounts.getTrucksAccounts(),
			transformResponse: (response: GetAccountsResponse) => response?.data
		}),
		getMerchandiseAccounts: builder.query<GetAccountsResponseData, void>({
			query: () => endpoints.accounts.getMerchandiseAccounts(),
			transformResponse: (response: GetAccountsResponse) => response?.data
		}),
		getBillToShipTos: builder.query<models["ShipToViewModel"][], object>({
			query: (args: BillToShipToParams) => {
				const { accountId, billToId } = args;
				return {
					url: endpoints.accounts.getBillToShipTos(accountId, billToId)
				};
			},
			transformResponse: (response: { data: models["ShipToViewModel"][] }) => response?.data,
			transformErrorResponse: (errors: any) => ({
				data: {
					code: errors?.code
				}
			})
		}),
		getBillToInfo: builder.query<BillToInfoResponseData, object>({
			query: (args: BillTosRequestParameters) => {
				const { accountId, billToId } = args;
				return {
					url: endpoints.accounts.getBillToInfo(accountId, billToId)
				};
			},
			transformResponse: (response: BillToInfoResponse) => response?.data,
			transformErrorResponse: (errors: any) => ({
				data: {
					code: errors?.code
				}
			}),
			providesTags: ["BillTo"]
		}),
		getEmulationAccounts: builder.query<GetEmulationAccountsResponseData, GetEmulationAccountsQueryParameters>({
			query: (queryParams?: GetEmulationAccountsQueryParameters) => ({
				url: endpoints.accounts.getEmulationAccounts(queryParams)
			}),
			transformResponse: (response: GetEmulationAccountsResponse) => response?.data,
			transformErrorResponse: (errors: any) => ({
				data: {
					code: errors?.code
				}
			})
		}),
		getMerchandiseShipTos: builder.query<MerchandiseShipTosResponseData, { accountId: string }>({
			query: ({ accountId }) => endpoints.accounts.getMerchandiseShipTos(accountId),
			transformResponse: (response: MerchandiseShipTosResponse) => response?.data,
			providesTags: ["MerchandiseShipTos"]
		}),
		addMerchandiseShipTo: builder.mutation<AddMerchandiseShipToResponseData, AddNewMerchandiseShipToRequest>({
			query: (request) => ({
				url: endpoints.accounts.addNewMerchandiseShipTo(),
				method: "POST",
				headers: {
					"Content-Type": "application/json"
				},
				body: encodePostRequest(request)
			}),
			transformResponse: (response: AddMerchandiseShipToResponse) => response?.data,
			invalidatesTags: ["MerchandiseShipTos"]
		})
	})
});

export const {
	useGetAccountsQuery,
	useLazyGetAccountsQuery,
	useGetTrucksAccountsQuery,
	useLazyGetEmulationAccountsQuery,
	useGetAccountsByIdQuery,
	useLazyGetAccountsByIdQuery,
	useGetBillToInfoQuery,
	useGetMerchandiseAccountsQuery,
	useAddMerchandiseShipToMutation,
	useGetMerchandiseShipTosQuery,
	useLazyGetMerchandiseShipTosQuery
} = accountApiSlice;
